import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { logger } from '@/logger';

interface PaginationShape {
	continuation_token?: string | null;
	has_more: boolean;
}

interface UseResourceListOptions<TItem, TPageResponse extends PaginationShape> {
	userId: string | null;
	selectedId: string | null;
	pageSize: number;
	logLabel: string;
	paginatedPath: (userId: string, params: URLSearchParams) => string;
	detailPath: (userId: string, id: string) => string;
	searchPath?: (userId: string, params: URLSearchParams) => string;
	extractItems: (response: TPageResponse) => TItem[];
	getId: (item: TItem) => string;
	errorMessages: {
		listLoadFailed: string;
		itemLoadFailed: string;
	};
	searchDebounceMs?: number;
}

interface UseResourceListReturn<TItem> {
	items: TItem[];
	hasMore: boolean;
	isLoading: boolean;
	isLoadingMore: boolean;
	error: string | null;
	selectedItem: TItem | null;
	isLoadingItem: boolean;
	itemError: string | null;
	searchQuery: string;
	searchResults: TItem[] | null;
	isSearching: boolean;
	setSearchQuery: (query: string) => void;
	loadMore: () => void;
	refresh: () => Promise<void>;
	updateItem: (item: TItem) => void;
	removeItem: (id: string) => void;
	prependItem: (item: TItem) => void;
}

export const useResourceList = <TItem, TPageResponse extends PaginationShape>(
	options: UseResourceListOptions<TItem, TPageResponse>,
): UseResourceListReturn<TItem> => {
	const {
		userId,
		selectedId,
		pageSize,
		logLabel,
		paginatedPath,
		detailPath,
		searchPath,
		extractItems,
		getId,
		errorMessages,
		searchDebounceMs = 300,
	} = options;

	const bridgewayApi = useBridgewayApi();

	const [items, setItems] = useState<TItem[]>([]);
	const [continuationToken, setContinuationToken] = useState<string | null>(
		null,
	);
	const [hasMore, setHasMore] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [selectedItem, setSelectedItem] = useState<TItem | null>(null);
	const [isLoadingItem, setIsLoadingItem] = useState(false);
	const [itemError, setItemError] = useState<string | null>(null);

	const [searchQuery, setSearchQueryState] = useState('');
	const [searchResults, setSearchResults] = useState<TItem[] | null>(null);
	const [isSearching, setIsSearching] = useState(false);
	const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

	const fetchPage = useCallback(
		async (token: string | null, append: boolean) => {
			if (!userId) {
				setItems([]);
				setContinuationToken(null);
				setHasMore(false);
				setError(null);
				return;
			}

			if (append) {
				setIsLoadingMore(true);
			} else {
				setIsLoading(true);
			}
			setError(null);
			try {
				logger.info(`Fetching ${logLabel} for user ${userId}`);
				const params = new URLSearchParams({ page_size: String(pageSize) });
				if (token) {
					params.set('continuation_token', token);
				}
				const response = await bridgewayApi.get<TPageResponse>(
					paginatedPath(userId, params),
				);
				const fetched = extractItems(response);
				logger.info(`Fetched ${fetched.length} ${logLabel}`);
				setItems((prev) => (append ? [...prev, ...fetched] : fetched));
				setContinuationToken(response.continuation_token ?? null);
				setHasMore(response.has_more);
			} catch (err) {
				logger.error(`Failed to fetch ${logLabel}`, { error: err });
				setError(errorMessages.listLoadFailed);
			} finally {
				if (append) {
					setIsLoadingMore(false);
				} else {
					setIsLoading(false);
				}
			}
		},
		[
			bridgewayApi,
			errorMessages.listLoadFailed,
			extractItems,
			logLabel,
			pageSize,
			paginatedPath,
			userId,
		],
	);

	useEffect(() => {
		void fetchPage(null, false);
	}, [fetchPage]);

	const loadMore = useCallback(() => {
		if (!continuationToken || isLoadingMore) return;
		void fetchPage(continuationToken, true);
	}, [continuationToken, fetchPage, isLoadingMore]);

	const refresh = useCallback(() => fetchPage(null, false), [fetchPage]);

	useEffect(() => {
		const fetchSelected = async () => {
			if (!userId || !selectedId) {
				setSelectedItem(null);
				setItemError(null);
				return;
			}

			setIsLoadingItem(true);
			setItemError(null);
			try {
				logger.info(`Fetching ${logLabel} ${selectedId} for user ${userId}`);
				const item = await bridgewayApi.get<TItem>(
					detailPath(userId, selectedId),
				);
				setSelectedItem(item);
			} catch (err) {
				logger.error(`Failed to fetch ${logLabel} item`, { error: err });
				setItemError(errorMessages.itemLoadFailed);
				setSelectedItem(null);
			} finally {
				setIsLoadingItem(false);
			}
		};

		void fetchSelected();
	}, [
		bridgewayApi,
		detailPath,
		errorMessages.itemLoadFailed,
		logLabel,
		selectedId,
		userId,
	]);

	const setSearchQuery = useCallback(
		(query: string) => {
			setSearchQueryState(query);
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

			if (!searchPath) {
				setSearchResults(null);
				setIsSearching(false);
				return;
			}

			if (!query.trim()) {
				setSearchResults(null);
				setIsSearching(false);
				return;
			}

			setIsSearching(true);
			searchTimerRef.current = setTimeout(async () => {
				if (!userId) return;
				try {
					const params = new URLSearchParams({
						q: query.trim(),
						page_size: String(pageSize),
					});
					const results = await bridgewayApi.get<TItem[]>(
						searchPath(userId, params),
					);
					setSearchResults(results);
				} catch (err) {
					logger.error(`Failed to search ${logLabel}`, { error: err });
					setSearchResults([]);
				} finally {
					setIsSearching(false);
				}
			}, searchDebounceMs);
		},
		[bridgewayApi, logLabel, pageSize, searchDebounceMs, searchPath, userId],
	);

	useEffect(() => {
		return () => {
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		};
	}, []);

	const updateItem = useCallback(
		(item: TItem) => {
			const id = getId(item);
			setItems((prev) => prev.map((it) => (getId(it) === id ? item : it)));
			setSearchResults((prev) =>
				prev === null ? prev : prev.map((it) => (getId(it) === id ? item : it)),
			);
			setSelectedItem((prev) =>
				prev !== null && getId(prev) === id ? item : prev,
			);
		},
		[getId],
	);

	const removeItem = useCallback(
		(id: string) => {
			setItems((prev) => prev.filter((it) => getId(it) !== id));
			setSelectedItem((prev) =>
				prev !== null && getId(prev) === id ? null : prev,
			);
		},
		[getId],
	);

	const prependItem = useCallback((item: TItem) => {
		setItems((prev) => [item, ...prev]);
	}, []);

	return {
		items,
		hasMore,
		isLoading,
		isLoadingMore,
		error,
		selectedItem,
		isLoadingItem,
		itemError,
		searchQuery,
		searchResults,
		isSearching,
		setSearchQuery,
		loadMore,
		refresh,
		updateItem,
		removeItem,
		prependItem,
	};
};
